// path: src/App.js
import React, { Component } from "react";
import AppBar from "@material-ui/core/AppBar";
import "./App.css";
import characters from "./Characters.json";
import Row from "./Components/row/Row.js";
import BottomNav from "./Components/BottomBar";
import { auth } from "./firebase";
import Home from "./Home";
import Collection from "./Collection";
import Recap from "./Recap";
import { setMaxUnlockedLevel } from "./utils/progression";
import { signOut } from "firebase/auth";
import {
  getUserHighScore,
  setUserHighScore,
  getUserCollection,
  setUserCollection,
} from "./firebaseUtils";

const APP_VERSION = "Alpha 1.5";

const LS_COLLECTION_KEY = "mmu_collection_unlocked_v1";

const normalizeId = (id) => {
  const n = Number(id);
  return Number.isFinite(n) ? n : id;
};

class App extends Component {
  componentDidMount() {
    // Load guest collection immediately (used if not logged in)
    this.loadGuestCollection();

    // Auth check
    auth.onAuthStateChanged(async (user) => {
      if (user) {
        const [savedTopScore, savedCollection] = await Promise.all([
          getUserHighScore(user.uid),
          getUserCollection(user.uid),
        ]);

        this.setState((prevState) => ({
          isAuthenticated: true,
          user,
          topScore: Math.max(prevState.topScore, savedTopScore),
          collectionUnlocked: Array.from(
            new Set([
              ...(prevState.collectionUnlocked || []),
              ...(savedCollection || []),
            ].map(normalizeId))
          ),
        }));
      } else {
        this.setState({
          isAuthenticated: false,
          user: null,
        });
      }
    });
  }

  highScoreSaveTimer = null;
  collectionSaveTimer = null;
  levelIntroTimer = null;

  componentDidUpdate(prevProps, prevState) {
    // Level intro animation gate
    if (prevState.currentLevel !== this.state.currentLevel) {
      if (this.levelIntroTimer) clearTimeout(this.levelIntroTimer);

      this.setState({ showLevelOverlay: true, isBoardVisible: false }, () => {
        this.levelIntroTimer = setTimeout(() => {
          this.setState({ showLevelOverlay: false, isBoardVisible: true });
        }, 800);
      });
    }

    // Save high score (signed-in)
    if (
      this.state.isAuthenticated &&
      this.state.user?.uid &&
      prevState.topScore !== this.state.topScore
    ) {
      if (this.highScoreSaveTimer) clearTimeout(this.highScoreSaveTimer);

      this.highScoreSaveTimer = setTimeout(() => {
        setUserHighScore(this.state.user.uid, this.state.topScore).catch((e) =>
          console.error("Failed to save high score:", e)
        );
      }, 400);
    }

    // Persist collection:
    // - guests -> localStorage
    // - signed-in -> Firestore (debounced)
    if (prevState.collectionUnlocked !== this.state.collectionUnlocked) {
      this.saveGuestCollection();

      if (this.state.isAuthenticated && this.state.user?.uid) {
        if (this.collectionSaveTimer) clearTimeout(this.collectionSaveTimer);
        this.collectionSaveTimer = setTimeout(() => {
          setUserCollection(this.state.user.uid, this.state.collectionUnlocked).catch(
            (e) => console.error("Failed to save collection:", e)
          );
        }, 500);
      }
    }
  }

  componentWillUnmount() {
    if (this.highScoreSaveTimer) clearTimeout(this.highScoreSaveTimer);
    if (this.collectionSaveTimer) clearTimeout(this.collectionSaveTimer);
    if (this.levelIntroTimer) clearTimeout(this.levelIntroTimer);
  }

  loadGuestCollection = () => {
    try {
      const raw = localStorage.getItem(LS_COLLECTION_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;

      this.setState({
        collectionUnlocked: Array.from(new Set(parsed.map(normalizeId))),
      });
    } catch {
      // ignore
    }
  };

  saveGuestCollection = () => {
    try {
      localStorage.setItem(
        LS_COLLECTION_KEY,
        JSON.stringify(this.state.collectionUnlocked || [])
      );
    } catch {
      // ignore
    }
  };

  startNextLevel = () => {
    const nextLevel = this.state.pendingNextLevel;
    if (nextLevel == null) return;

    if (nextLevel >= this.levels.length) {
      this.setState({
        screen: "recap",
        runOutcome: "win",
        runLevelReached: this.levels.length,
        showLevelCompleteOverlay: false,
        pendingNextLevel: null,
      });
      return;
    }

    const fetchedNewCards = this.getNewCards(this.levels[nextLevel].cards);

    this.setState(
      {
        showLevelCompleteOverlay: false,
        pendingNextLevel: null,
        currentLevel: nextLevel,
        cards: this.shuffleArray(fetchedNewCards).slice(0, this.levels[nextLevel].cards),
        clicked: Array(this.levels[nextLevel].cards).fill(false),
      },
      () => {
        // unlock by reaching this level (levels are 1-based for storage)
        setMaxUnlockedLevel(nextLevel + 1);
      }
    );
  };

  // Define levels
  levels = [
    { cards: 4, threshold: 4 },
    { cards: 4, threshold: 8 },
    { cards: 9, threshold: 17 },
    { cards: 9, threshold: 26 },
    { cards: 9, threshold: 35 },
    { cards: 16, threshold: 51 },
    { cards: 16, threshold: 67 },
    { cards: 16, threshold: 83 },
    { cards: 16, threshold: 99 },
    { cards: 16, threshold: 115 },
  ];

  constructor(props) {
    super(props);
    this.state = {
      isAuthenticated: false,
      user: null,

      // simple screen state: "home" | "game" | "collection"
      screen: "home",

      showLevelOverlay: false,
      isBoardVisible: true,
      showLevelCompleteOverlay: false,
      pendingNextLevel: null,

      cards: this.shuffleArray(characters).slice(0, this.levels[0].cards),
      score: 0,
      topScore: 0,
      clicked: Array(this.levels[0].cards).fill(false),
      currentLevel: 0,
      usedCards: [],

      // Collection state
      collectionUnlocked: [],

      // Run recap stats
      runCorrectClicks: 0,
      runLevelsCompleted: 0,
      runNewUnlocks: 0,
      runOutcome: "loss", // "win" | "loss"
      runLevelReached: 1,
    };
  }

  // Fisher-Yates Shuffle on characters
  shuffleArray = (array) => {
    let shuffled = array.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Fisher-Yates Shuffle on cards, update state
  shuffle = () => {
    const c = [...this.state.cards];
    for (let i = c.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [c[i], c[j]] = [c[j], c[i]];
    }
    this.setState({ cards: c });
  };

  findId = (id) => {
    return this.state.clicked.includes(id);
  };

  returnFirstNull = () => {
    return this.state.clicked.indexOf(false);
  };

  insertId = (id, index) => {
    this.setState({
      clicked: this.state.clicked.map((elem, i) => (i === index ? id : elem)),
    });
  };

  emptyClicked = () => {
    this.setState({
      clicked: Array(this.state.cards.length).fill(false),
    });
  };

  resetScoreZero = () => {
    this.setState({ score: 0 });
  };

  // Get new cards each level
  getNewCards = (num) => {
    const newCards = [];
    const newlyUsedIndices = [];

    while (newCards.length < num) {
      const randomIndex = Math.floor(Math.random() * characters.length);

      const alreadyUsed = this.state.usedCards.includes(randomIndex);
      const pickedThisCall = newlyUsedIndices.includes(randomIndex);

      if (!alreadyUsed && !pickedThisCall) {
        newCards.push(characters[randomIndex]);
        newlyUsedIndices.push(randomIndex);
      }
    }

    this.setState((prevState) => ({
      usedCards: [...prevState.usedCards, ...newlyUsedIndices],
    }));

    return newCards;
  };

  // Unlock logic placeholder:
  // If the click is valid (not already clicked this run), mark that card as "obtained".
  unlockCardIfNeeded = (rawId) => {
    const id = normalizeId(rawId);
    this.setState((prev) => {
      const current = prev.collectionUnlocked || [];
      if (current.includes(id)) return null;
      return {
        collectionUnlocked: [...current, id],
        runNewUnlocks: (prev.runNewUnlocks || 0) + 1,
      };
    });
  };

  // Main game logic
  handleClick = (event) => {
    const id = event.target.id; // string from DOM

    if (this.findId(id)) {
      // LOSS: run ended due to losing criteria
      this.setState({
        screen: "recap",
        runOutcome: "loss",
        runLevelReached: this.state.currentLevel + 1,
        showLevelCompleteOverlay: false,
        pendingNextLevel: null,
      });
      return;
    } else {
      // valid click -> unlock for collection
      this.unlockCardIfNeeded(id);
      
      this.setState((prev) => ({
        runCorrectClicks: (prev.runCorrectClicks || 0) + 1,
      }));

      this.insertId(id, this.returnFirstNull());

      const newScore = this.state.score + 1;
      let newTopScore = this.state.topScore;

      if (newScore > this.state.topScore) {
        newTopScore = newScore;
      }

      this.setState({
        score: newScore,
        topScore: newTopScore,
      });

      this.shuffle();

      if (newScore >= this.levels[this.state.currentLevel].threshold) {
        const nextLevel = this.state.currentLevel + 1;
        this.setState({
          showLevelCompleteOverlay: true,
          pendingNextLevel: nextLevel,
          runLevelsCompleted: (this.state.runLevelsCompleted || 0) + 1,
        });
        return;
      }
    }
  };

  resetGameToStart = () => {
    this.setState({
      showLevelOverlay: false,
      isBoardVisible: true,
      showLevelCompleteOverlay: false,
      pendingNextLevel: null,

      cards: this.shuffleArray(characters).slice(0, this.levels[0].cards),
      score: 0,
      topScore: this.state.topScore,
      clicked: Array(this.levels[0].cards).fill(false),
      currentLevel: 0,
      usedCards: [],
    });
  };

  goHome = () => {
    this.resetGameToStart();
    this.setState({ screen: "home" });
  };

  goCollection = () => {
    this.setState({ screen: "collection" });
  };

  goPlay = ({ startLevel = 1, startScore = 0 } = {}) => {
  // App uses 0-based level index internally
  const startIndex = Math.max(0, Math.min(this.levels.length - 1, startLevel - 1));
  const cardsCount = this.levels[startIndex].cards;

  // fresh deck for the chosen level
  const startingCards = this.shuffleArray(characters).slice(0, cardsCount);

    this.setState({
      showLevelOverlay: false,
      isBoardVisible: true,
      showLevelCompleteOverlay: false,
      pendingNextLevel: null,

      screen: "game",

      // run stats reset
      runCorrectClicks: 0,
      runLevelsCompleted: 0,
      runNewUnlocks: 0,
      runOutcome: "loss",
      runLevelReached: startIndex + 1,

      // actual gameplay state
      cards: startingCards,
      score: startScore,
      topScore: this.state.topScore,
      clicked: Array(cardsCount).fill(false),
      currentLevel: startIndex,
      usedCards: [],
      },
      () => setMaxUnlockedLevel(startIndex + 1)
    );
  };

  handleLogout = async () => {
    try {
      await signOut(auth);
    } finally {
      this.goHome();
      this.setState({ isAuthenticated: false });
    }
  };

  render() {
    // HOME screen
    if (this.state.screen === "home") {
      return (
        <Home
          isAuthenticated={this.state.isAuthenticated}
          user={this.state.user}
          onPlay={this.goPlay}
          onCollection={this.goCollection}
          onLoginSuccess={() => this.setState({ isAuthenticated: true })}
          onLogout={this.handleLogout}
        />
      );
    }

    // COLLECTION screen
    if (this.state.screen === "collection") {
      return (
        <>
          <AppBar position="fixed" color="secondary">
            <nav className="navbar navbar-default myNavColor">
              <div className="container-fluid">
                <div className="navRow">
                  <div className="navLeft">
                    <h2 className="whiteText impactFont navTitle">Memory Mix-Up</h2>

                    <button className="homeNavButton" onClick={this.goHome}>
                      Home
                    </button>

                    <button className="homeNavButton" onClick={this.goPlay}>
                      Play
                    </button>
                  </div>

                  <div className="navRight whiteText">
                    <h4>
                      Collection: {(this.state.collectionUnlocked || []).length}/
                      {characters.length}
                    </h4>
                  </div>
                </div>
              </div>
            </nav>
          </AppBar>

          <Collection
            unlockedIds={this.state.collectionUnlocked}
            onBackHome={this.goHome}
          />

          <BottomNav className="bottomNav">
            <div className="appVersion">{APP_VERSION}</div>
          </BottomNav>
        </>
      );
    }

    // RECAP screen
    if (this.state.screen === "recap") {
      return (
        <Recap
          outcome={this.state.runOutcome}
          levelsCompleted={this.state.runLevelsCompleted}
          levelReached={this.state.runLevelReached}
          correctClicks={this.state.runCorrectClicks}
          newlyUnlocked={this.state.runNewUnlocks}
          totalUnlocked={(this.state.collectionUnlocked || []).length}
          totalCards={characters.length}
          onPlayAgain={this.goPlay}
          onMainMenu={this.goHome}
        />
      );
    }

    // GAME screen
    const totalCards = this.state.cards.length;
    const cardsPerRow = Math.min(4, Math.ceil(Math.sqrt(totalCards)));
    document.documentElement.style.setProperty("--cards-per-row", cardsPerRow);

    return (
      <>
        {this.state.showLevelCompleteOverlay && (
          <div className="levelCompleteOverlay" role="dialog" aria-modal="true">
            <div className="levelCompleteOverlay__panel">
              <div className="levelCompleteOverlay__title">
                Level {this.state.currentLevel + 1} Complete!
              </div>

              <button
                className="levelCompleteOverlay__button"
                onClick={this.startNextLevel}
              >
                Start Level{" "}
                {(this.state.pendingNextLevel ?? this.state.currentLevel + 1) + 1}
              </button>
            </div>
          </div>
        )}

        {this.state.showLevelOverlay && (
          <div className="levelOverlay" role="status" aria-live="polite">
            <div className="levelOverlay__panel">
              <div className="levelOverlay__label">Level</div>
              <div className="levelOverlay__value">{this.state.currentLevel + 1}</div>
            </div>
          </div>
        )}

        <AppBar position="fixed" color="secondary">
          <nav className="navbar navbar-default myNavColor">
            <div className="container-fluid">
              <div className="navRow">
                <div className="navLeft">
                  <h2 className="whiteText impactFont navTitle">Memory Mix-Up</h2>

                  <button className="homeNavButton" onClick={this.goHome}>
                    Home
                  </button>

                  <button className="homeNavButton" onClick={this.goCollection}>
                    Collection
                  </button>
                </div>

                <div className="navRight whiteText">
                  <h4>
                    Current Score: {this.state.score} || High Score: {this.state.topScore}
                    <span className="levelTracker">
                      {" "}
                      || Level: {this.state.currentLevel + 1}
                    </span>
                  </h4>
                </div>
              </div>
            </div>
          </nav>
        </AppBar>

        <div id="main-content" className="container">
          {this.state.isBoardVisible && !this.state.showLevelCompleteOverlay && (
            <div
              key={this.state.currentLevel}
              className="gameBoard gameBoard--levelEnter"
            >
              {this.state.cards.map((card, index) => (
                <Row
                  {...card}
                  handleClick={this.handleClick}
                  index={index}
                  levelKey={this.state.currentLevel}
                  key={card.id}
                />
              ))}
            </div>
          )}
        </div>

        <BottomNav className="bottomNav">
          <div className="appVersion">{APP_VERSION}</div>
        </BottomNav>
      </>
    );
  }
}

export default App;
